import { useState } from 'react'
import * as React from 'react'
import { useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Store,
  DollarSign,
  AlertTriangle,
  CarFront,
  MapPin,
  Wrench,
  Package,
  TrendingUp,
  Battery,
  Zap,
  CreditCard,
  Bike,
  Building2,
  Shield,
  Globe,
  Truck,
  ShoppingBag,
  BarChart3,
  FileText,
  ShieldCheck,
  BookOpen,
  Receipt,
} from 'lucide-react'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuth } from '../../contexts/AuthContext'

const drawerWidth = 240
const collapsedDrawerWidth = 72

interface SidebarProps {
  open: boolean
  onClose: () => void
  collapsed?: boolean
}

interface MenuItem {
  text: string
  icon: React.ReactNode
  path?: string
  children?: MenuItem[]
  // Used only by the Spare Parts role-assignment bootstrap screen.
  alwaysVisible?: boolean
  moduleKey?: ModuleKey
  // Permission requirements - if not specified, item is always visible
  requiredPermission?: {
    service: string
    resource: string
    action: string
  }
  // For items that should only be visible to super admins
  superAdminOnly?: boolean
  // For items that require ANY of multiple permissions
  anyOfPermissions?: Array<{
    service: string
    resource: string
    action: string
  }>
}

type ModuleKey =
  | 'dashboard'
  | 'riders'
  | 'vehicles'
  | 'warRoom'
  | 'cities'
  | 'inventory'
  | 'spareParts'
  | 'clients'
  | 'teams'
  | 'documentation'
  | 'profile'
  | 'admin'

const moduleAccessByDepartment: Record<string, ModuleKey[]> = {
  CENTRAL: ['dashboard', 'riders', 'vehicles', 'warRoom', 'clients', 'inventory', 'spareParts', 'teams', 'profile', 'documentation'],
  OPS: ['riders', 'vehicles', 'warRoom', 'inventory', 'spareParts', 'profile'],
  FIN: ['dashboard', 'riders', 'vehicles', 'inventory', 'spareParts', 'clients', 'teams', 'profile'],
  SUPPLY: ['dashboard', 'riders', 'vehicles', 'warRoom', 'cities', 'clients', 'inventory', 'spareParts', 'profile'],
  HR: ['riders', 'teams', 'profile', 'documentation'],
  TECH: ['dashboard', 'riders', 'vehicles', 'warRoom', 'cities', 'clients', 'inventory', 'spareParts', 'teams', 'profile', 'documentation', 'admin'],
  MKT: ['dashboard', 'riders', 'vehicles', 'warRoom', 'clients', 'teams', 'profile'],
  MIS: ['dashboard', 'riders', 'vehicles', 'clients', 'inventory', 'spareParts', 'profile'],
}

const departmentCodeByEmail: Record<string, string> = {
  'admin@ev91.com': 'TECH',
  'manager@ev91.com': 'OPS',
  'operator@ev91.com': 'OPS',
  'fieldagent@ev91.com': 'OPS',
  'hr@ev91.com': 'HR',
  'finance@ev91.com': 'FIN',
  'sales@ev91.com': 'CENTRAL',
  'marketing@ev91.com': 'MKT',
  'test@ev91.com': 'MIS',
}

const departmentCodeByRole: Record<string, string> = {
  Central_User: 'CENTRAL',
  EV_Operations_User: 'OPS',
  EV_Operations_Head: 'OPS',
  EV_Operations_Worker: 'OPS',
  Finance_User: 'FIN',
  HR_User: 'HR',
  IT_Admin: 'TECH',
  Marketing_User: 'MKT',
  MIS_User: 'MIS',
  Supply_User: 'SUPPLY',
}

const menuItems: MenuItem[] = [
  // 1. Dashboard
  {
    text: 'Dashboard',
    icon: <LayoutDashboard size={20} />,
    path: '/',
    moduleKey: 'dashboard',
    requiredPermission: {
      service: 'auth',
      resource: 'dashboard',
      action: 'read'
    }
  },
  // 2. Rider Management (with separate Rider List and Rider Earnings)
  {
    text: 'Riders',
    icon: <Users size={20} />,
    moduleKey: 'riders',
    anyOfPermissions: [
      { service: 'rider', resource: 'riders', action: 'read' },
      { service: 'rider', resource: 'riders', action: 'manage' },
      { service: 'rider', resource: 'kyc', action: 'read' },
      { service: 'rider', resource: 'kyc', action: 'verify' },
      { service: 'rider', resource: 'earnings', action: 'read' }
    ],
    children: [
      {
        text: 'Riders',
        icon: <Users size={20} />,
        path: '/rider-management',
        anyOfPermissions: [
          { service: 'rider', resource: 'riders', action: 'read' },
          { service: 'rider', resource: 'riders', action: 'manage' },
          { service: 'rider', resource: 'kyc', action: 'read' },
          { service: 'rider', resource: 'kyc', action: 'verify' }
        ]
      },
      {
        text: 'Rider Vehicle Analytics',
        icon: <BarChart3 size={20} />,
        path: '/rider-management/vehicle-analytics',
        anyOfPermissions: [
          { service: 'rider', resource: 'riders', action: 'read' },
          { service: 'rider', resource: 'riders', action: 'manage' }
        ]
      },
      {
        text: 'Rider Attendance Analytics',
        icon: <BarChart3 size={20} />,
        path: '/rider-management/attendance-analytics',
        anyOfPermissions: [
          { service: 'rider', resource: 'riders', action: 'read' },
          { service: 'rider', resource: 'riders', action: 'manage' }
        ]
      },
      // {
      //   text: 'Vehicle History',
      //   icon: <FileText size={20} />,
      //   path: '/rider-management/vehicle-history',
      //   anyOfPermissions: [
      //     { service: 'rider', resource: 'riders', action: 'read' },
      //     { service: 'rider', resource: 'riders', action: 'manage' }
      //   ]
      // },
      // {
      //   text: 'Earnings',
      //   icon: <DollarSign size={20} />,
      //   path: '/rider-earnings',
      //   anyOfPermissions: [
      //     { service: 'rider', resource: 'earnings', action: 'read' },
      //     { service: 'rider', resource: 'riders', action: 'read' },
      //     { service: 'rider', resource: 'riders', action: 'manage' }
      //   ]
      // },
      {
        text: 'Orders',
        icon: <ShoppingBag size={20} />,
        path: '/orders',
        anyOfPermissions: [
          { service: 'rider', resource: 'orders', action: 'read' },
          { service: 'rider', resource: 'orders', action: 'manage' }
        ]
      },
      {
        text: 'Rider Tickets',
        icon: <AlertTriangle size={20} />,
        path: '/rider-tickets',
        anyOfPermissions: [
          { service: 'rider', resource: 'riders', action: 'read' },
          { service: 'rider', resource: 'riders', action: 'manage' }
        ]
      },

      {
        text: 'MIS',
        icon: <FileText size={20} />,
        path: '/mis',
        anyOfPermissions: [
          { service: 'rider', resource: 'mis', action: 'read' },
          { service: 'rider', resource: 'mis', action: 'manage' },
          { service: 'rider', resource: 'earnings', action: 'read' },
          { service: 'rider', resource: 'earnings', action: 'manage' }
        ]
      },
      {
        text: 'Payouts',
        icon: <CreditCard size={20} />,
        path: '/payouts',
        anyOfPermissions: [
          { service: 'rider', resource: 'payouts', action: 'read' },
          { service: 'rider', resource: 'payouts', action: 'manage' },
          { service: 'rider', resource: 'earnings', action: 'read' }
        ]
      },
      {
        text: 'Collections',
        icon: <DollarSign size={20} />,
        path: '/manual-collections',
        anyOfPermissions: [
          { service: 'rider', resource: 'manual-collections', action: 'read' },
          { service: 'rider', resource: 'manual-collections', action: 'manage' },
          { service: 'rider', resource: 'collections', action: 'read' },
          { service: 'rider', resource: 'collections', action: 'manage' },
          { service: 'rider', resource: 'earnings', action: 'read' },
          { service: 'rider', resource: 'earnings', action: 'manage' }
        ]
      },
      {
        text: 'Deficit Details',
        icon: <AlertTriangle size={20} />,
        path: '/deficit-details',
        anyOfPermissions: [
          { service: 'rider', resource: 'manual-collections', action: 'read' },
          { service: 'rider', resource: 'manual-collections', action: 'manage' },
          { service: 'rider', resource: 'collections', action: 'read' },
          { service: 'rider', resource: 'collections', action: 'manage' },
          { service: 'rider', resource: 'earnings', action: 'read' },
          { service: 'rider', resource: 'earnings', action: 'manage' }
        ]
      },
      // {
      //   text: 'Security Deposits',
      //   icon: <ShieldCheck size={20} />,
      //   path: '/security-deposit',
      //   anyOfPermissions: [
      //     { service: 'rider', resource: 'earnings', action: 'read' },
      //     { service: 'rider',   resource: 'earnings', action: 'manage' },
      //     { service: 'rider', resource: 'riders', action: 'manage' }
      //   ]
      // },
      {
        text: 'SD Setup',
        icon: <ShieldCheck size={20} />,
        path: '/security-deposit-config',
        anyOfPermissions: [
          { service: 'rider', resource: 'security-deposits', action: 'read' },
          { service: 'rider', resource: 'security-deposits', action: 'manage' },
          { service: 'rider', resource: 'earnings', action: 'manage' },
          { service: 'rider', resource: 'riders', action: 'manage' }
        ]
      },
      {
        text: 'SD Transactions',
        icon: <Receipt size={20} />,
        path: '/paylink-transactions',
        anyOfPermissions: [
          { service: 'rider', resource: 'earnings', action: 'read' },
          { service: 'rider', resource: 'riders', action: 'manage' }
        ]
      },
      {
        text: 'Refund',
        icon: <DollarSign size={20} />,
        path: '/refund',
        anyOfPermissions: [
          { service: 'rider', resource: 'security-deposit-config', action: 'read' },
          { service: 'rider', resource: 'security-deposit-config', action: 'manage' },
          { service: 'rider', resource: 'earnings', action: 'manage' },
          { service: 'rider', resource: 'riders', action: 'manage' }
        ]
      }
    ]
  },
  // 4. Vehicle Management
  {
    text: 'Vehicles',
    icon: <Zap size={20} />,
    moduleKey: 'vehicles',
    anyOfPermissions: [
      { service: 'vehicle', resource: 'vehicles', action: 'read' },
      { service: 'vehicle', resource: 'maintenance', action: 'read' },
      { service: 'vehicle', resource: 'models', action: 'read' },
      { service: 'vehicle', resource: 'oems', action: 'read' },
      { service: 'vehicle', resource: 'hubs', action: 'read' },
      { service: 'vehicle', resource: 'cities', action: 'read' },
      { service: 'vehicle', resource: 'damage', action: 'read' },
      { service: 'vehicle', resource: 'documents', action: 'read' },
      { service: 'vehicle', resource: 'media', action: 'read' },
      { service: 'vehicle', resource: 'analytics', action: 'read' },
      { service: 'vehicle', resource: 'fleet', action: 'read' },
      { service: 'vehicle', resource: 'tracking', action: 'read' }
    ],
    children: [
      {
        text: 'Dashboard',
        icon: <LayoutDashboard size={20} />,
        path: '/vehicle-dashboard',
        requiredPermission: {
          service: 'vehicle',
          resource: 'vehicles',
          action: 'read'
        }
      },
      {
        text: 'Vehicles',
        icon: <Bike size={20} />,
        path: '/vehicles',
        requiredPermission: {
          service: 'vehicle',
          resource: 'vehicles',
          action: 'read'
        }
      },
      {
        text: 'Models',
        icon: <CarFront size={20} />,
        path: '/vehicle-models',
        requiredPermission: {
          service: 'vehicle',
          resource: 'models',
          action: 'read'
        }
      },
      {
        text: 'Hubs',
        icon: <MapPin size={20} />,
        path: '/hubs',
        requiredPermission: {
          service: 'vehicle',
          resource: 'hubs',
          action: 'read'
        }
      },
      {
        text: 'Services',
        icon: <Wrench size={20} />,
        path: '/services',
        requiredPermission: {
          service: 'vehicle',
          resource: 'maintenance',
          action: 'read'
        }
      },
      /*
      {
        text: 'Unified Service Management',
        icon: <UnifiedServiceIcon />,
        path: '/unified-service',
        anyOfPermissions: [
          { service: 'vehicle', resource: 'maintenance', action: 'read' },
          { service: 'spare-parts', resource: 'service-requests', action: 'read' },
          { service: 'vehicle', resource: 'service-requests', action: 'read' }
        ]
      },
      */ // DISABLED - Unified Service Management
      {
        text: 'Damage',
        icon: <AlertTriangle size={20} />,
        path: '/damage',
        requiredPermission: {
          service: 'vehicle',
          resource: 'damage',
          action: 'read'
        }
      },
    ],
  },

  // 🔥 War Room Management
  {
    text: 'War Room Management',
    icon: <Globe size={20} />,
    moduleKey: 'warRoom',
    children: [
      {
        text: 'War Room',
        icon: <Globe size={20} />,
        path: '/war-room',
        requiredPermission: {
          service: 'war-room',
          resource: 'vehicles',
          action: 'read'
        }
      },
      {
        text: 'War Room Charts',
        icon: <BarChart3 size={20} />,
        path: '/war-room/charts',
        requiredPermission: {
          service: 'war-room',
          resource: 'charts',
          action: 'read'
        }
      },
      {
        text: 'Vehicle Summary',
        icon: <BarChart3 size={20} />,
        path: '/warroom-summary',
        requiredPermission: {
          service: 'war-room',
          resource: 'summary',
          action: 'read'
        }
      },
      {
        text: 'Distance Analytics',
        icon: <BarChart3 size={20} />,
        path: '/distance-analytics',
        requiredPermission: {
          service: 'war-room',
          resource: 'distance-analytics',
          action: 'read'
        }
      }
    ],
  },


  // 5. Spare Parts Management
  {
    text: 'Inventory',
    icon: <Package size={20} />,
    alwaysVisible: true,
    moduleKey: 'inventory',
    anyOfPermissions: [
      { service: 'spare-parts', resource: 'parts', action: 'read' },
      { service: 'spare-parts', resource: 'inventories', action: 'read' },
      { service: 'spare-parts', resource: 'suppliers', action: 'read' },
      { service: 'spare-parts', resource: 'purchase-orders', action: 'read' },
      { service: 'spare-parts', resource: 'analytics', action: 'read' },
      { service: 'spare-parts', resource: 'dashboard', action: 'read' },
      { service: 'vehicle', resource: 'batteries', action: 'read' },
      { service: 'vehicle', resource: 'chargers', action: 'read' },
      { service: 'auth', resource: 'employees', action: 'read' },
      { service: 'spare-parts', resource: 'dashboard', action: 'read' },
      { service: 'spare-parts', resource: 'dashboard', action: 'manage' },
      { service: 'spare-parts', resource: 'analytics', action: 'read' },
      { service: 'spare-parts', resource: 'parts', action: 'read' },
      { service: 'spare-parts', resource: 'inventories', action: 'read' }
    ],
    children: [
      {
        text: 'Spare Parts',
        icon: <Package size={20} />,
        path: '/spare-parts',
        moduleKey: 'spareParts',
        anyOfPermissions: [
          { service: 'spare-parts', resource: 'dashboard', action: 'read' },
          { service: 'spare-parts', resource: 'dashboard', action: 'manage' },
          { service: 'spare-parts', resource: 'analytics', action: 'read' },
          { service: 'spare-parts', resource: 'parts', action: 'read' },
          { service: 'spare-parts', resource: 'inventories', action: 'read' }
        ]
      },
      {
        text: 'Spare Analytics',
        icon: <BarChart3 size={20} />,
        path: '/spare-parts/analytics',
        moduleKey: 'spareParts',
        anyOfPermissions: [
          { service: 'spare-parts', resource: 'analytics', action: 'read' },
          { service: 'spare-parts', resource: 'dashboard', action: 'read' }
        ]
      },
      {
        text: 'Spare Maintenance',
        icon: <Wrench size={20} />,
        path: '/spare-parts/maintenance',
        moduleKey: 'spareParts',
        anyOfPermissions: [
          { service: 'spare-parts', resource: 'maintenance', action: 'read' },
          { service: 'spare-parts', resource: 'maintenance', action: 'manage' },
          { service: 'spare-parts', resource: 'dashboard', action: 'read' }
        ]
      },
      {
        text: 'Spare Reports',
        icon: <FileText size={20} />,
        path: '/spare-parts/reports',
        moduleKey: 'spareParts',
        anyOfPermissions: [
          { service: 'spare-parts', resource: 'analytics', action: 'read' },
          { service: 'spare-parts', resource: 'dashboard', action: 'read' }
        ]
      },
      {
        text: 'Spare Operations',
        icon: <Truck size={20} />,
        path: '/spare-parts/operations',
        moduleKey: 'spareParts',
        anyOfPermissions: [
          { service: 'spare-parts', resource: 'inventories', action: 'read' },
          { service: 'spare-parts', resource: 'inventories', action: 'manage' },
          { service: 'spare-parts', resource: 'dashboard', action: 'read' }
        ]
      },
      {
        text: 'Spare Parts Roles',
        icon: <ShieldCheck size={20} />,
        path: '/spare-parts/maintenance-assignments',
        alwaysVisible: true,
      },
      {
        text: 'Batteries',
        icon: <Battery size={20} />,
        path: '/batteries',
        requiredPermission: {
          service: 'vehicle',
          resource: 'batteries',
          action: 'read'
        }
      },
      {
        text: 'Chargers',
        icon: <Zap size={20} />,
        path: '/chargers',
        requiredPermission: {
          service: 'vehicle',
          resource: 'chargers',
          action: 'read'
        }
      },
    ],
  },
  // 6. Order Management - DISABLED
  /*
  {
    text: 'Order Management',
    icon: <OrderIcon />,
    anyOfPermissions: [
      { service: 'order', resource: 'orders', action: 'read' },
      { service: 'order', resource: 'orders', action: 'manage' },
      { service: 'order', resource: 'orders', action: 'create' }
    ],
    children: [
      {
        text: 'All Orders',
        icon: <OrderIcon />,
        path: '/orders',
        requiredPermission: {
          service: 'order',
          resource: 'orders',
          action: 'read'
        }
      },
      {
        text: 'Create New Order',
        icon: <OrderIcon />,
        path: '/orders/new',
        requiredPermission: {
          service: 'order',
          resource: 'orders',
          action: 'create'
        }
      }
    ]
  },
  */
  // 7. Clients & Stores
  {
    text: 'Clients',
    icon: <Store size={20} />,
    moduleKey: 'clients',
    anyOfPermissions: [
      { service: 'client-store', resource: 'clients', action: 'read' },
      { service: 'client-store', resource: 'stores', action: 'read' }
    ],
    children: [
      {
        text: 'Clients',
        icon: <Store size={20} />,
        path: '/clients',
        anyOfPermissions: [
          { service: 'client-store', resource: 'clients', action: 'read' },
          { service: 'client-store', resource: 'stores', action: 'read' }
        ]
      },
      {
        text: 'Client Rider Mapping',
        icon: <UserCircle size={20} />,
        path: '/client-rider-mappings',
        anyOfPermissions: [
          { service: 'client-store', resource: 'clients', action: 'read' },
          { service: 'rider', resource: 'riders', action: 'read' }
        ]
      }
    ]
  },
  // 9. Team and Department
  {
    text: 'Teams',
    icon: <Building2 size={20} />,
    path: '/teams',
    moduleKey: 'teams',
    anyOfPermissions: [
      { service: 'auth', resource: 'teams', action: 'read' },
      { service: 'auth', resource: 'teams', action: 'manage' },
      { service: 'auth', resource: 'departments', action: 'read' },
      { service: 'auth', resource: 'departments', action: 'manage' }
    ]
  },
  // 10. City Management
  {
    text: 'Cities',
    icon: <Globe size={20} />,
    moduleKey: 'cities',
    anyOfPermissions: [
      { service: 'vehicle', resource: 'cities', action: 'read' },
      { service: 'vehicle', resource: 'cities', action: 'manage' },
      { service: 'vehicle', resource: 'cities', action: 'create' },
      { service: 'vehicle', resource: 'cities', action: 'update' },
      { service: 'vehicle', resource: 'cities', action: 'delete' }
    ],
    children: [
      {
        text: 'City Dashboard',
        icon: <LayoutDashboard size={20} />,
        path: '/city-dashboard',
        requiredPermission: {
          service: 'vehicle',
          resource: 'cities',
          action: 'read'
        }
      },
      {
        text: 'Cities',
        icon: <MapPin size={20} />,
        path: '/cities',
        requiredPermission: {
          service: 'vehicle',
          resource: 'cities',
          action: 'read'
        }
      },
    ],
  },
  // 11. Roles and Permissions
  {
    text: 'Roles & Permissions',
    icon: <Shield size={20} />,
    path: '/roles',
    moduleKey: 'admin',
    anyOfPermissions: [
      { service: 'auth', resource: 'roles', action: 'read' },
      { service: 'auth', resource: 'permissions', action: 'read' }
    ]
  },
  // 12. API Documentation
  {
    text: 'API Documentation',
    icon: <BookOpen size={20} />,
    path: '/api-docs',
    moduleKey: 'documentation',
    requiredPermission: {
      service: 'auth',
      resource: 'api-docs',
      action: 'read'
    }
  },
  // 13. Profile
  {
    text: 'Profile',
    icon: <UserCircle size={20} />,
    path: '/profile',
    moduleKey: 'profile',
    requiredPermission: {
      service: 'auth',
      resource: 'profile',
      action: 'read'
    }
  },
  // 14. Settings - DISABLED
  /*
  {
    text: 'Settings',
    icon: <Settings size={20} />,
    path: '/settings',
    superAdminOnly: true
  },
  */
]

export default function Sidebar({ open, onClose, collapsed = false }: SidebarProps) {
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const { hasPermission, hasAnyOfPermissions, isSuperAdmin, getUserPermissions } = usePermissions()
  const { user } = useAuth()

  const getDepartmentCode = (): string | undefined => {
    const userAny = user as any
    const code =
      userAny?.employee?.department?.code ||
      userAny?.department?.code ||
      userAny?.departmentCode

    if (code) {
      return String(code).toUpperCase()
    }

    const departmentName =
      userAny?.employee?.department?.name ||
      userAny?.department?.name ||
      userAny?.department

    if (typeof departmentName === 'string') {
      const normalizedName = departmentName.toLowerCase()

      if (normalizedName.includes('central')) return 'CENTRAL'
      if (normalizedName.includes('ev operations') || normalizedName.includes('operations')) return 'OPS'
      if (normalizedName.includes('finance')) return 'FIN'
      if (normalizedName.includes('hr') || normalizedName.includes('human resources')) return 'HR'
      if (normalizedName.includes('it') || normalizedName.includes('technology')) return 'TECH'
      if (normalizedName.includes('marketing')) return 'MKT'
      if (normalizedName.includes('mis')) return 'MIS'
    }

    const roleDepartmentCode = user?.roles
      ?.map(role => departmentCodeByRole[role.name])
      .find(Boolean)

    if (roleDepartmentCode) return roleDepartmentCode

    return user?.email ? departmentCodeByEmail[user.email.toLowerCase()] : undefined
  }

  const getDepartmentModules = (): ModuleKey[] => {
    const departmentCode = getDepartmentCode()

    if (!departmentCode) return []

    return moduleAccessByDepartment[departmentCode] || []
  }

  const canAccessModule = (moduleKey: ModuleKey): boolean => {
    if (isSuperAdmin()) return true

    return getDepartmentModules().includes(moduleKey)
  }

  // Debug logging for permission issues
  React.useEffect(() => {
    console.log('🐛 Sidebar Debug - User:', user?.email, user?.firstName, user?.lastName)
    console.log('🐛 Sidebar Debug - User roles:', user?.roles?.map(r => r.name))
    console.log('🐛 Sidebar Debug - Is Super Admin:', isSuperAdmin())
    console.log('🐛 Sidebar Debug - User permissions count:', getUserPermissions().length)
    console.log('🐛 Sidebar Debug - First 10 permissions:', getUserPermissions().slice(0, 10).map(p => `${p.service}:${p.resource}:${p.action}`))

    // Test specific permissions
    const testPermissions = [
      { service: 'auth', resource: 'users', action: 'read' },
      { service: 'auth', resource: 'teams', action: 'read' },
      { service: 'client-store', resource: 'clients', action: 'read' },
      { service: 'vehicle', resource: 'vehicles', action: 'read' },
      { service: 'spare-parts', resource: 'parts', action: 'read' }
    ]

    console.log('🐛 Sidebar Debug - Permission tests:')
    testPermissions.forEach(perm => {
      const hasIt = hasPermission(perm.service, perm.resource, perm.action)
      console.log(`   ${hasIt ? '✅' : '❌'} ${perm.service}:${perm.resource}:${perm.action}`)
    })
  }, [user, hasPermission, getUserPermissions, isSuperAdmin])

  // Filter menu items based on permissions
  const filterMenuItems = (items: MenuItem[], parentModuleKey?: ModuleKey): MenuItem[] => {
    return items
      .filter(item => {
        // The role-assignment screen must be reachable before a Spare Parts
        // role or city is assigned. Other Inventory items remain unchanged.
        if (item.alwaysVisible) {
          return true
        }

        // Super Admin has access to everything - bypass all checks
        if (isSuperAdmin()) {
          console.log(`🐛 Super Admin access granted for: ${item.text}`)
          return true
        }

        const moduleKey = item.moduleKey || parentModuleKey

        if (moduleKey) {
          return canAccessModule(moduleKey)
        }

        // Check super admin requirement
        if (item.superAdminOnly && !isSuperAdmin()) {
          console.log(`🐛 Super admin required, access denied for: ${item.text}`)
          return false
        }

        // Check single permission requirement
        if (item.requiredPermission) {
          const { service, resource, action } = item.requiredPermission
          const hasIt = hasPermission(service, resource, action)
          console.log(`🐛 ${item.text} - Required permission ${service}:${resource}:${action}: ${hasIt ? '✅' : '❌'}`)
          return hasIt
        }

        // Check multiple permission requirements (any of)
        if (item.anyOfPermissions) {
          const hasAny = hasAnyOfPermissions(item.anyOfPermissions)
          console.log(`🐛 ${item.text} - Any of permissions: ${hasAny ? '✅' : '❌'}`)
          return hasAny
        }

        // If no permission requirements, show the item
        console.log(`🐛 ${item.text} - No permission requirements, showing`)
        return true
      })
      .map(item => ({
        ...item,
        // Recursively filter children
        children: item.children
          ? filterMenuItems(item.children, item.moduleKey || parentModuleKey)
          : undefined
      }))
      .filter(item => {
        // Remove items with children if all children are filtered out
        if (item.children) {
          return item.children.length > 0
        }
        return true
      })
  }

  const filteredMenuItems = filterMenuItems(menuItems)

  console.log(`🐛 Sidebar Debug - Filtered menu items (${filteredMenuItems.length}):`, filteredMenuItems.map(item => item.text))

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [menuChildren, setMenuChildren] = React.useState<MenuItem[]>([])

  const handleToggleExpand = (itemText: string) => {
    setExpandedItems(prev =>
      prev.includes(itemText)
        ? prev.filter(item => item !== itemText)
        : [...prev, itemText]
    )
  }

  const handleParentClick = (event: React.MouseEvent<HTMLElement>, item: MenuItem) => {
    if (collapsed && item.children && item.children.length > 0) {
      // Show popover menu when collapsed
      setAnchorEl(event.currentTarget)
      setMenuChildren(item.children)
    } else if (!collapsed) {
      // Toggle expand when not collapsed
      handleToggleExpand(item.text)
    }
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuChildren([])
  }

  const isPathSelected = (path?: string, children?: MenuItem[]) => {
    if (path) {
      return location.pathname === path
    }
    if (children) {
      return children.some(child => child.path && location.pathname === child.path)
    }
    return false
  }

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.text)
    const isSelected = isPathSelected(item.path, item.children)

    // When collapsed, don't render child items (depth > 0)
    if (collapsed && depth > 0) {
      return null
    }

    if (hasChildren) {
      const button = (
        <ListItemButton
  onMouseEnter={(e) => {
    if (collapsed && item.children && item.children.length > 0) {
      setAnchorEl(e.currentTarget)
      setMenuChildren(item.children)
    }
  }}
  onClick={(e) => {
    if (!collapsed) {
      handleParentClick(e, item)
    }
  }}
          selected={isSelected}
          sx={{
            pl: collapsed ? 2 : 2 + depth * 2,
            justifyContent: collapsed ? 'center' : 'initial',
            minHeight: 48,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main + '20',
              color: theme.palette.primary.main,
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          <ListItemIcon sx={{
            minWidth: collapsed ? 'auto' : 40,
            justifyContent: 'center',
            mr: collapsed ? 0 : 2,
          }}>
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText primary={item.text} />
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </>
          )}
        </ListItemButton>
      );

      return (
        <Box key={item.text}>
          <ListItem disablePadding>
            {collapsed ? (
              <Tooltip title={item.text} placement="right" arrow>
                {button}
              </Tooltip>
            ) : (
              button
            )}
          </ListItem>
          {!collapsed && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.children?.map(child => renderMenuItem(child, depth + 1))}
              </List>
            </Collapse>
          )}
        </Box>
      )
    }

    const button = (
      <ListItemButton
        component={RouterLink}
        to={item.path!}
        selected={isSelected}
        onClick={isMobile ? onClose : undefined}
        sx={{
          pl: collapsed ? 2 : 2 + depth * 2,
          justifyContent: collapsed ? 'center' : 'initial',
          minHeight: 48,
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main + '20',
            color: theme.palette.primary.main,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
            },
          },
        }}
      >
        <ListItemIcon sx={{
          minWidth: collapsed ? 'auto' : 40,
          justifyContent: 'center',
          mr: collapsed ? 0 : 2,
        }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && <ListItemText primary={item.text} />}
      </ListItemButton>
    );

    return (
      <ListItem key={item.text} disablePadding>
        {collapsed ? (
          <Tooltip title={item.text} placement="right" arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    )
  }

  const drawer = (
    <Box>
      <Toolbar>
        {!collapsed && (
          <Typography variant="h6" noWrap component="div" color="primary" fontWeight="bold">
            EV91 Dashboard
          </Typography>
        )}
        {collapsed && (
          <Typography variant="h6" noWrap component="div" color="primary" fontWeight="bold" textAlign="center" width="100%">
            EV91
          </Typography>
        )}
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map(item => renderMenuItem(item))}
      </List>

      {/* Popover menu for collapsed sidebar */}
<Menu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={handleMenuClose}
  MenuListProps={{
    onMouseLeave: handleMenuClose,
  }}
  anchorOrigin={{
    vertical: 'top',
    horizontal: 'right',
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'left',
  }}
  sx={{
    ml: 0,
    pointerEvents: 'none',
  }}
  slotProps={{
    paper: {
      onMouseEnter: () => {},
      onMouseLeave: handleMenuClose,
      sx: {
        pointerEvents: 'auto',
      },
    },
  }}
>
        {menuChildren.map((child) => (
          <MenuItem
            key={child.text}
            component={RouterLink}
            to={child.path!}
            onClick={handleMenuClose}
            selected={location.pathname === child.path}
            sx={{
              minWidth: 200,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main + '20',
                color: theme.palette.primary.main,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {child.icon}
            </ListItemIcon>
            <ListItemText primary={child.text} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )

  return (
    <Box
      component="nav"
      sx={{ width: { lg: collapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { lg: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            transition: theme.transitions.create(['width'], {
  easing: theme.transitions.easing.easeInOut,
  duration: 200,
}),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  )
}
